import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatChatDisplayName } from '../../utils/nameFormatters';
import { useBackTarget } from '../../hooks/useBackTarget';
import { renderSupportSystemMessage, SupportClosedBanner } from '../../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDirectChatPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const location = useLocation();
  const { convoId } = useParams();
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [chatName, setChatName] = useState(formatChatDisplayName(location.state?.chatName) || location.state?.chatName || 'Conversation');
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [markingSolved, setMarkingSolved] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const messagesEndRef = useRef(null);

  const logAdminChatDebug = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }, []);

  const fetchConversation = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/conversations/${convoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(response.data.messages || []);
      setConversation(response.data.conversation || null);
      setChatName(formatChatDisplayName(response.data.other_user) || formatChatDisplayName(location.state?.chatName) || location.state?.chatName || 'Conversation');
    } catch (error) {
      logAdminChatDebug('Failed to load admin chat:', error);
      toast.error(error.response?.data?.detail || 'Failed to load admin chat');
    } finally {
      setLoading(false);
    }
  }, [convoId, location.state?.chatName, logAdminChatDebug]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  useEffect(() => {
    const handler = (event) => {
      if (event?.detail?.conversation_id && event.detail.conversation_id !== convoId) return;
      fetchConversation();
    };
    window.addEventListener('allure:conversation-message-received', handler);
    return () => window.removeEventListener('allure:conversation-message-received', handler);
  }, [convoId, fetchConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isSupportConvo = conversation?.type === 'support';
  const supportStatus = conversation?.support_status || null;
  const supportClosed = isSupportConvo && supportStatus === 'closed';
  const canMarkSolved = isSupportConvo && supportStatus === 'open';

  const handleSend = async () => {
    if (!draft.trim() || supportClosed) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/messages/send`, { conversation_id: convoId, content: draft.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages((prev) => [...prev, response.data]);
      setDraft('');
    } catch (error) {
      logAdminChatDebug('Failed to send admin message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send admin message');
    }
  };

  const handleMarkSolved = async () => {
    if (markingSolved) return;
    setMarkingSolved(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/support/conversations/${convoId}/mark-solved`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Sent — asking the user to confirm resolution.');
      await fetchConversation();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not mark solved');
    } finally {
      setMarkingSolved(false);
    }
  };

  return (
    <div className="app-shell admin-scope" data-testid="admin-direct-chat-page" style={{ backgroundColor: '#F8F8F8', minHeight: '100vh' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={goBack} data-testid="admin-direct-chat-back-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ChevronLeft size={28} color="var(--text)" />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{chatName}</p>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
            {isSupportConvo ? `Support · ${supportStatus === 'closed' ? 'Done' : supportStatus === 'awaiting_close_confirm' ? 'Awaiting user' : 'Open'}` : 'Admin direct conversation'}
          </p>
        </div>
        {canMarkSolved ? (
          <button
            type="button"
            onClick={handleMarkSolved}
            disabled={markingSolved}
            data-testid="admin-direct-chat-mark-solved-button"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 999,
              border: 'none',
              background: markingSolved ? '#B7B7B7' : '#1F8E2A',
              color: '#FFFFFF',
              fontWeight: 700,
              fontSize: 12.5,
              fontFamily: "'Manrope', sans-serif",
              cursor: markingSolved ? 'not-allowed' : 'pointer',
              flexShrink: 0,
            }}
          >
            <CheckCircle2 size={13} strokeWidth={2.4} />
            {markingSolved ? 'Asking…' : 'Mark solved'}
          </button>
        ) : null}
      </div>

      <div style={{ padding: '20px 16px 120px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <p style={{ textAlign: 'center', color: '#999' }}>Loading chat…</p>
        ) : messages.map((message) => {
          const isMine = message.sender_id === user.id;
          const messageKey = `${message.id}-${message.created_at || 'msg'}`;
          const supportRender = renderSupportSystemMessage(message, {
            conversation,
            isMine,
            onResolved: () => fetchConversation(),
            dataTestId: `admin-chat-support-${messageKey}`,
          });
          if (supportRender) return supportRender.node;
          return (
            <div key={message.id} style={{ alignSelf: isMine ? 'flex-end' : 'flex-start', maxWidth: '78%', background: 'var(--surface)', borderRadius: 18, padding: '12px 14px', boxShadow: 'var(--premium-shadow-main)', border: '1px solid var(--card-border)', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
              <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)' }}>{isMine ? 'Admin' : chatName}</p>
              <p style={{ margin: '4px 0 0', fontSize: 15, color: 'var(--text)', lineHeight: 1.35, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{message.content}</p>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {supportClosed ? (
        <div style={{ position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', left: '50%', transform: 'translateX(-50%)', width: 'calc(100% - 32px)', maxWidth: 380 }}>
          <SupportClosedBanner
            onOpenNewTicket={() => navigate('/admin')}
            dataTestId="admin-direct-chat-support-closed-banner"
          />
        </div>
      ) : (
        <div style={{ position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)', left: '50%', transform: 'translateX(-50%)', width: 'calc(100% - 32px)', maxWidth: 360, background: 'var(--surface)', border: '1px solid var(--card-border)', borderRadius: 9999, boxShadow: 'var(--premium-shadow-main)', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px' }}>
          <input value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') handleSend(); }} data-testid="admin-direct-chat-input" placeholder="Message" style={{ flex: 1, border: 'none', outline: 'none', fontFamily: "'Inria Serif', serif", fontSize: 16, background: 'transparent', color: 'var(--text)' }} />
          <button onClick={handleSend} data-testid="admin-direct-chat-send-button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
            <Send size={18} color="var(--accent-purple)" />
          </button>
        </div>
      )}
    </div>
  );
}

